import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, X, AlertCircle } from 'lucide-react';
import { Button } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { useAuth } from '../context/AuthContext';
import { createArtwork } from '../services/artworkService';
import { uploadArtworkImage } from '../services/storageService';
import { Category } from '../types';

const PostArtwork: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Category>('Painting');
  const [price, setPrice] = useState('');
  const [sizes, setSizes] = useState<string[]>([]);
  const [sizeInput, setSizeInput] = useState('');
  const [sizeError, setSizeError] = useState('');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const categories: Category[] = ['Painting', 'Sculpture', 'Digital Art', 'Design', 'Handcraft'];

  const MAX_IMAGES = 4;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const combined = [...imageFiles, ...files].slice(0, MAX_IMAGES);
    setImageFiles(combined);

    // generate previews
    const readers = combined.map(file => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    });

    Promise.all(readers).then(previews => setImagePreviews(previews));
  };

  // Validate size format (allows: 12x16, 12x16 inches, 12 x 16, etc.)
  const validateSizeFormat = (size: string): boolean => {
    if (!size.trim()) return false;
    
    // Allow formats like: 12x16, 12 x 16, 12x16 inches, 12x16 cm, 12"x16", etc.
    const validPatterns = [
      /^\d+\s*[x×]\s*\d+\s*(inches?|in|cm|ft|feet|mm)?$/i,  // 12x16, 12x16 inches
      /^\d+["']\s*[x×]\s*\d+["']$/,                          // 12"x16"
      /^\d+\.\d+\s*[x×]\s*\d+\.\d+\s*(inches?|in|cm|ft|feet|mm)?$/i, // 12.5x16.5
    ];
    
    return validPatterns.some(pattern => pattern.test(size.trim()));
  };

  const addSize = () => {
    const trimmedSize = sizeInput.trim();
    
    if (!trimmedSize) {
      setSizeError('');
      return;
    }

    // Check if already exists
    if (sizes.includes(trimmedSize)) {
      setSizeError('This size is already added');
      return;
    }

    // Validate format
    if (!validateSizeFormat(trimmedSize)) {
      setSizeError('Invalid format. Use formats like: 12x16, 12x16 inches, 12 x 16 cm');
      return;
    }

    // Add the size
    setSizes([...sizes, trimmedSize]);
    setSizeInput('');
    setSizeError('');
  };

  const removeSize = (size: string) => {
    setSizes(sizes.filter(s => s !== size));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSizeError('');
    
    // Auto-add pending size input if it exists
    if (sizeInput.trim()) {
      const trimmedSize = sizeInput.trim();
      
      // Validate the pending size
      if (!validateSizeFormat(trimmedSize)) {
        setSizeError('Invalid size format. Use formats like: 12x16, 12x16 inches, 12 x 16 cm');
        setError('Please fix the size format error before posting');
        return;
      }
      
      // Check if it's not a duplicate
      if (!sizes.includes(trimmedSize)) {
        // Auto-add it
        setSizes([...sizes, trimmedSize]);
        setSizeInput('');
      }
      
      // Small delay to let state update, then proceed with submission
      setTimeout(() => submitArtwork([...sizes, trimmedSize]), 100);
      return;
    }
    
    // No pending size input, proceed normally
    submitArtwork(sizes);
  };

  const submitArtwork = async (finalSizes: string[]) => {
    if (imageFiles.length === 0) {
      setError('Please select at least one image');
      return;
    }

    if (!user) {
      setError('You must be logged in');
      return;
    }

    setLoading(true);
    
    try {
      // Upload images
      const imageUrls = await Promise.all(
        imageFiles.map((file) => uploadArtworkImage(file, user.id))
      );

      // Create artwork (store first image as imageUrl for backward compatibility)
      await createArtwork({
        title,
        description,
        category,
        imageUrl: imageUrls[0],
        imageUrls: imageUrls,
        price: parseFloat(price),
        sizes: finalSizes.length > 0 ? finalSizes : undefined,
        artistId: user.id,
        artistName: user.name,
        artistAvatar: user.avatar,
        isPromoted: false,
      });
      
      alert('Artwork posted successfully!');
      navigate('/');
    } catch (err: any) {
      console.error('Error posting artwork:', err);
      setError(err.message || 'Failed to post artwork');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto pb-24">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Post New Artwork</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Image Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Artwork Image *
          </label>
          <div>
            <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary transition-colors">
              <Upload size={48} className="text-gray-400 mb-2" />
              <span className="text-gray-500">Click or drop up to 4 images</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                className="hidden"
              />
            </label>

            {imagePreviews.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {imagePreviews.map((src, idx) => (
                  <div key={idx} className="relative">
                    <img src={src} alt={`Preview ${idx + 1}`} className="w-full h-40 object-cover rounded-lg" />
                    <button
                      type="button"
                      onClick={() => {
                        const newFiles = imageFiles.filter((_, i) => i !== idx);
                        const newPreviews = imagePreviews.filter((_, i) => i !== idx);
                        setImageFiles(newFiles);
                        setImagePreviews(newPreviews);
                      }}
                      className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Title */}
        <Input
          label="Title *"
          type="text"
          placeholder="Enter artwork title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            placeholder="Describe your artwork"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
          />
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Category *
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
            required
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Price */}
        <Input
          label="Price (₹) *"
          type="number"
          placeholder="Enter price"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          min="0"
          step="0.01"
        />

        {/* Sizes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Available Sizes (optional)
          </label>
          <div className="flex gap-2 mb-2">
            <div className="flex-1">
              <input
                type="text"
                placeholder="e.g., 12x16 inches, 36x36 cm"
                value={sizeInput}
                onChange={(e) => {
                  setSizeInput(e.target.value);
                  setSizeError(''); // Clear error on typing
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addSize();
                  }
                }}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none ${
                  sizeError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {sizeError && (
                <div className="flex items-start gap-1 mt-1 text-xs text-red-600">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{sizeError}</span>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={addSize}
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              Add
            </button>
          </div>
          
          {/* Size format examples */}
          <p className="text-xs text-gray-500 mb-2">
            💡 Accepted formats: 12x16, 12x16 inches, 12 x 16 cm, 12"x16"
          </p>
          
          {/* Added sizes */}
          {sizes.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {sizes.map((size) => (
                <div
                  key={size}
                  className="flex items-center gap-2 px-3 py-1 bg-primary text-white rounded-full"
                >
                  <span>{size}</span>
                  <button
                    type="button"
                    onClick={() => removeSize(size)}
                    className="hover:bg-indigo-900 rounded-full p-1 transition-colors"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          {/* Pending size notice */}
          {sizeInput.trim() && !sizeError && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-xs text-blue-700">
                ℹ️ Size "{sizeInput}" will be automatically added when you post
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm flex items-start gap-2">
            <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Submit */}
        <Button type="submit" fullWidth disabled={loading}>
          {loading ? 'Posting...' : 'Post Artwork'}
        </Button>
      </form>
    </div>
  );
};

export default PostArtwork;